import React from "react";
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardSubtitle,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonToolbar,
  useIonPopover,
} from "@ionic/react";
import { useState } from "react";
import { useContext } from "react";
import { AppContext } from "../AppContext";
import { useEffect } from "react";
import { getSettings, updateSettings } from "../../lib/Store";
import {
  VideoConverterPresetProps,
  VideoConverterProps,
} from "../../interfaces/core";
import { defaultPresets } from "../../lib/video-converter/presets";
import { ellipse, informationCircleOutline } from "ionicons/icons";
import { chooseArrTpe } from "../../lib/video-converter/logic";
import { modalController } from "@ionic/core";
import { useTranslation } from "react-i18next";

const VideoConverterPresets: React.FC<{ setSettings }> = ({ setSettings }) => {
  const presetsKey = "video-converter-presets";
  const { store } = useContext(AppContext);
  const [savedPresets, setSavedPresets] = useState([]);

  const getPresets = async () => {
    await getSettings({
      store: store,
      key: presetsKey,
      setValue: (value) => value && setSavedPresets(value),
    });
  };
  useEffect(() => {
    getPresets();
  }, []);

  return (
    <div className="w-full grid md:grid-cols-2">
      {defaultPresets.length > 0 &&
        defaultPresets.map((preset) => (
          <VCPresetCard
            key={`preset.${preset.name}`}
            {...preset}
            {...{ store, setSettings }}
          />
        ))}
    </div>
  );
};
const getPreview = (preset) => {
  const width = window.innerWidth > 768 ? 6 : 5;
  let preview = [];
  const fg =
    preset.pixel.type === "circle"
      ? preset.pixel.options.forgroundColor
      : preset.pixel.type === "character"
      ? preset.pixel.options.characterColor
      : undefined;
  const pixelType = preset.pixel.type;
  for (let y = 0; y < preset.display.dpi / 12; y++) {
    let row = [];
    for (let x = 0; x < preset.display.dpi / width; x++) {
      let selectedArr;
      if (pixelType === "character") {
        selectedArr = chooseArrTpe(
          preset.pixel.options.character.split(","),
          preset.pixel.options.displayMode,
          1000,
          preset.display.dpi,
          x,
          y
        );
      } else if (pixelType === "emoji") {
        selectedArr = chooseArrTpe(
          preset.pixel.options.source,
          preset.pixel.options.displayMode,
          1000,
          preset.display.dpi,
          x,
          y
        );
      } else if (pixelType === "circle") {
        selectedArr = <IonIcon icon={ellipse} />;
      }

      row.push(<div key={`${preset.name}.${y}.${x}`}>{selectedArr}</div>);
    }
    preview.push(
      <div key={`${preset.name}.${y}`}>
        <div
          className={`flex flex-row-reverse justify-around ${
            preset.display.dpi === 60
              ? "font-medium"
              : preset.display.dpi === 72 || preset.display.dpi === 96
              ? "text-sm font-medium"
              : preset.display.dpi >= 120
              ? "text-[0.45rem] font-medium"
              : "font-semibold text-lg"
          }`}
          style={{ color: fg ? `hsl(${fg.h},${fg.s}%,${fg.l}%)` : "white" }}
        >
          {row}
        </div>
      </div>
    );
  }
  return preview;
};
const VCPresetCard: React.FC<
  { store: Storage | any; setSettings: (s) => void } & VideoConverterPresetProps
> = ({ name, preset, type, store, setSettings }) => {
  const bg = preset.display.background;
  let preview = getPreview(preset);
  return (
    <IonCard className="relative rounded-lg" key={`preset.${name}`}>
      <div
        style={{ backgroundColor: `hsl(${bg.h},${bg.s}%,${bg.l}%)` }}
        className="h-32 w-full p-2 flex justify-around flex-col"
      >
        {preview}
      </div>
      <PresetToolbar {...{ name, preset, type, store, setSettings }} />
    </IonCard>
  );
};
const PopOver = ({
  handleDelete,
  dismissPop,
  preset,
  type,
}: {
  handleDelete: () => void;
  dismissPop: () => void;
  preset: VideoConverterProps;
  type: string;
}) => {
  const { t } = useTranslation();
  return (
    <IonList>
      <IonItem>
        <IonLabel>{t(`Video Converter.Audio`)}</IonLabel>
        <IonLabel slot="end">
          {preset.audio && preset.audio.enabled ? "Enabled" : "Disabled"}
        </IonLabel>
      </IonItem>
      <IonItem>
        <IonLabel>DPI</IonLabel>
        <IonLabel slot="end">{preset.display.dpi}</IonLabel>
      </IonItem>
      <IonItem className="capitalize">
        <IonLabel>{t(`Video Converter.Pixel Type`)}</IonLabel>
        <IonLabel slot="end">{preset.pixel.type}</IonLabel>
      </IonItem>
      {(preset.pixel.type === "character" || preset.pixel.type === "emoji") && (
        <IonItem className="capitalize">
          <IonLabel>{t(`Video Converter.Mode`)}</IonLabel>
          <IonLabel slot="end">{preset.pixel.options.displayMode}</IonLabel>
        </IonItem>
      )}
      {type === "custom" && (
        <IonItem
          onClick={() => {
            handleDelete();
            dismissPop();
          }}
          button
          detail={false}
        >
          <IonLabel color="danger">{t(`Delete...`)}</IonLabel>
        </IonItem>
      )}
    </IonList>
  );
};
const PresetToolbar = ({ name, preset, type, store, setSettings }) => {
  const { t } = useTranslation();
  const handleDelete = () => {
    // console.log(name, "will be deleted");
    dismissPop();
  };
  const [presentPop, dismissPop] = useIonPopover(PopOver, {
    type,
    preset,
    handleDelete: handleDelete,
    dismissPop: () => dismissPop(),
  });

  const handleUsePreset = async () => {
    await updateSettings({
      key: "video-converter-settings",
      value: preset,
      store,
    });
    setSettings(preset);
    modalController.dismiss();
  };
  return (
    <IonToolbar>
      <IonButtons slot="start">
        <IonButton
          onClick={(e) =>
            presentPop({ event: e.nativeEvent, cssClass: "large-popover" })
          }
        >
          <IonIcon color="medium" icon={informationCircleOutline} />
        </IonButton>
      </IonButtons>
      <IonCardSubtitle className="text-xs">{name}</IonCardSubtitle>
      <IonButtons slot="end">
        <IonButton color="primary" onClick={handleUsePreset}>
          {t(`Use`)}
        </IonButton>
      </IonButtons>
    </IonToolbar>
  );
};
export default VideoConverterPresets;
